/**
 * Creating an Agent Operating Procedure: describe it, watch it compile, review
 * the steps, create. One way forward — Next writes the procedure and its
 * scenario, and Cancel deletes them.
 *
 * Next has to write because `POST /compile` needs a procedure to exist, and
 * the scenario goes with it because there is no `DELETE /procedures/{id}` and
 * nothing lists procedures — so an unlinked one is invisible and permanent.
 * The cost is that the scope is picked before any step is seen.
 */

import { Alert, Button, Drawer, Form, Input, Modal, Popconfirm, Spin, Tooltip } from 'antd';
import { CheckCircleFilled, CloseOutlined } from '@ant-design/icons';
import React, { useCallback, useState } from 'react';
import { connect } from 'react-redux';
import {
	AOPDraft,
	AOPDraftPlan,
	AOPScenarioConflict,
	AOPScope,
	AOPStep,
	AOPUnsupportedStep,
} from '../../types/AOPTypes';
import OWButton from '../ow_button/OWButton';
import SelectWithAllToggle from '../common/SelectWithAllToggle';
import {
	compileProcedureDraft,
	createProcedure,
	writeWholeProcedureDraft,
} from '../../thunks/agent_procedures_thunks';
import {
	checkScenarioConflicts,
	createAgentScenario,
	deleteAgentScenario,
	describeScenarioError,
} from '../../thunks/agent_scenarios_thunks';
import { AOPActiveWindowField, validateActiveWindow } from './AOPActiveWindowField';
import {
	AOPSuggestionNote,
	AOPViolationNote,
	suggestionsFor,
	unanchoredSuggestions,
	unanchoredViolations,
	violationsFor,
} from './AOPNotes';
import { AOPPrioritySelect, AOPProblemTypeSelect } from './AOPScopeFields';
import { stepNumberOf, stepTitle } from './utils/AOPPlanUtils';
import { AOPNotExecutable, AOPUnsupportedDetail } from './AOPUnsupportedDetail';
import InlineHyperlink from '../common/InlineHyperlink';
import { withUserType } from '../withUserType';
import { nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import './AOPBuilder.less';

type Stage = 'form' | 'generating' | 'review';

const FORM_NAME = 'create-aop-form';

/** Decoration: the compile is one request with no progress to report. */
const GENERATING_STAGES = [
	'Reading your description',
	'Working out the steps',
	'Wiring the branches together',
	'Checking Radar can run it',
];

const countWords = (text: string): number =>
	text.trim().length === 0 ? 0 : text.trim().split(/\s+/).length;

const toScope = (isAll: boolean, ids: number[]): AOPScope => (isAll ? 'all' : ids ? ids : []);

/**
 * The description, wherever it is shown — one control over one piece of state,
 * rather than the form holding a copy that had to be pushed back by hand.
 */
const ProseColumn: React.FC<{
	prose: string;
	/** Left out inside a `Form.Item`, which draws its own. */
	label?: string;
	rows: number;
	onChangeProse?: (value: string) => void;
	action?: React.ReactNode;
}> = ({ prose, label, rows, onChangeProse, action }) => (
	<div className="flex flex-col gap-2xs">
		{label ? <span className="footnoteregular text-character-secondary">{label}</span> : null}
		<Input.TextArea
			rows={rows}
			value={prose}
			readOnly={!onChangeProse}
			aria-label="What should Radar do?"
			onChange={(event) => onChangeProse && onChangeProse(event.target.value)}
		/>
		<div className="flex items-center justify-between">
			<span className="footnoteregular text-character-secondary">{countWords(prose)} words</span>
			{action}
		</div>
	</div>
);

interface AOPCreateDrawerProps {
	/** `didWrite` tells the list behind this whether its page is now stale. */
	onClose: (didWrite: boolean) => void;
	onCreated: (procedureId: string) => void;
	compile: (procedureId: string, prose: string) => Promise<AOPDraft>;
	create: (name: string) => Promise<any>;
	createScenario: (input: any) => Promise<any>;
	/** Removes the scenario and, with it, the procedure it routes to. */
	removeScenario: (scenarioId: string) => Promise<void>;
	checkConflicts: (scope: any) => Promise<AOPScenarioConflict[]>;
	saveDraft: (
		procedureId: string,
		prose: string,
		plan: AOPDraftPlan,
		accept: string[]
	) => Promise<any>;
	/** The two scope slices, for turning a conflict's ids back into names. */
	problemTypes: any;
	priorities: any;
	userType: string;
}

export const AOPCreateDrawer: React.FC<AOPCreateDrawerProps> = ({
	onClose,
	onCreated,
	compile,
	create,
	createScenario,
	removeScenario,
	checkConflicts,
	saveDraft,
	problemTypes,
	priorities,
	userType,
}) => {
	const [form] = Form.useForm();
	const [stage, setStage] = useState<Stage>('form');
	/** Shown in the header once Next has settled it. */
	const [createdName, setCreatedName] = useState<string | null>(null);
	const [prose, setProse] = useState('');
	// The procedure exists from the moment Next succeeds: compiling writes into
	// its draft, so there is nothing to compile against until it does.
	const [procedureId, setProcedureId] = useState<string | null>(null);
	// Kept beside the procedure id: a second Next updates the pair rather than
	// making another, and the scenario is what carries the scope.
	const [scenarioId, setScenarioId] = useState<string | null>(null);
	const [plan, setPlan] = useState<AOPDraftPlan | null>(null);
	const [draft, setDraft] = useState<AOPDraft | null>(null);
	// Suggestions approved here, sent with the draft when the flowchart is made.
	const [accepted, setAccepted] = useState<string[]>([]);
	const [isSaving, setIsSaving] = useState(false);
	const [error, setError] = useState<string | null>(null);
	/** The description is not a Form field, so its refusal is held here. */
	const [proseError, setProseError] = useState<string | null>(null);
	const [conflicts, setConflicts] = useState<AOPScenarioConflict[]>([]);

	const suggestions = draft ? draft.suggestions : [];
	const steps = plan ? plan.steps : [];
	/** A plan is one step at minimum upstream, so the last one cannot go. */
	const canRemoveStep = steps.length > 1;
	const unsupportedCount = steps.filter((step) => step.type === 'unsupported').length;

	/**
	 * Violations date from the compile and the edits here are local, so two
	 * kinds are dropped rather than shown wrongly: one about a step since
	 * removed, and `unsupported_step` on a step since replaced. Violations these
	 * edits *create* are not invented — the canvas shows what the service makes
	 * of the draft.
	 */
	const violations = (draft ? draft.violations : []).filter((violation) => {
		if (!violation.anchor) {
			return true;
		}

		const anchored = steps.filter((step) => step.name === violation.anchor)[0];

		if (!anchored) {
			return false;
		}

		return violation.rule !== 'unsupported_step' || anchored.type === 'unsupported';
	});
	const planSuggestions = unanchoredSuggestions(suggestions);
	const planViolations = unanchoredViolations(violations);
	const outstanding = suggestions.filter(
		(suggestion) => suggestion.status === 'open' && accepted.indexOf(suggestion.id) < 0
	).length;

	const takeDraft = useCallback((record: AOPDraft) => {
		setDraft(record);
		setPlan(record.plan);
		// Compiling replaces the suggestions wholesale, so rulings made against the
		// previous set name ids that no longer exist. Carrying them over would send
		// the service a list of strangers.
		setAccepted([]);
		setStage('review');
	}, []);

	const runCompile = useCallback(
		(id: string, text: string) => {
			setError(null);
			setStage('generating');

			compile(id, text)
				.then(takeDraft)
				.catch(() => {
					// Forward, never back: the pair exists by the time anything compiles,
					// so the form would offer a Next that creates a second scenario.
					// Review without a plan is the description and Regenerate.
					setStage('review');
					setError('Radar could not turn that description into steps.');
				});
		},
		[compile, takeDraft]
	);

	/**
	 * Writes once; there is no way back to this form afterwards.
	 *
	 * The scope is checked before anything is written, so the only writes
	 * attempted are ones expected to succeed — a procedure created and then
	 * refused its scenario is one nothing can list or remove.
	 */
	const onNext = useCallback(() => {
		const described = prose.trim().length > 0;

		if (!described) {
			setProseError('Describe what Radar should do.');
		}

		form
			.validateFields()
			.then((fields) => {
				if (!described) {
					return undefined;
				}

				const scope = {
					problem_type_ids: toScope(fields.allProblemTypes, fields.problemTypeIds),
					priority_ids: toScope(fields.allPriorities, fields.priorityIds),
					active_window: fields.activeWindow,
				};

				setError(null);
				setConflicts([]);
				setIsSaving(true);

				return checkConflicts(scope)
					.then((found) => {
						if (found.length > 0) {
							setIsSaving(false);
							setConflicts(found);
							return undefined;
						}

						const name = fields.name.trim();

						setCreatedName(name);

						/*
						  Reused, not replaced. Next is only reachable twice after a failed
						  write, so the retry carries the same values — and a second procedure
						  would strand the first permanently, since delete runs through the
						  scenario it never got.
						*/
						const writeProcedure: Promise<string> = procedureId
							? Promise.resolve(procedureId)
							: create(name).then((procedure) => {
									setProcedureId(procedure.id);
									return procedure.id;
							  });

						return writeProcedure
							.then((id) =>
								createScenario({ procedure_id: id, ...scope }).then((scenario) => {
									setScenarioId(scenario.id);
									setIsSaving(false);
									runCompile(id, prose);
								})
							)
							.catch((code) => {
								setIsSaving(false);
								setError(
									typeof code === 'string' && code.indexOf('scenario') >= 0
										? describeScenarioError(code)
										: 'Could not create the procedure.'
								);
							});
					})
					.catch(() => {
						setIsSaving(false);
						setError('Could not check whether that scope is free.');
					});
			})
			.catch(() => undefined);
	}, [checkConflicts, create, createScenario, form, procedureId, prose, runCompile]);

	/**
	 * Exits pointing at the removed step are left alone: the service reports
	 * each as a dangling_exit, which is a clearer prompt to rewire than a branch
	 * retargeted somewhere nobody chose.
	 */
	const removeStep = (stepId: string) => {
		if (!plan || !canRemoveStep) {
			return;
		}

		setPlan({ steps: plan.steps.filter((step) => step.name !== stepId) });
	};

	/** Swap an unsupported step for one of its alternatives, keeping its name. */
	const replaceStep = (stepId: string, alternative: AOPStep) => {
		if (!plan) {
			return;
		}

		setPlan({
			steps: plan.steps.map((step) =>
				step.name === stepId ? { ...alternative, name: stepId } : step
			),
		});
	};

	/** The compile already wrote the draft; this saves the rulings on it. */
	const onCreateFlowchart = () => {
		if (!plan || !procedureId || plan.steps.some((step) => step.type === 'unsupported')) {
			return;
		}

		setIsSaving(true);
		saveDraft(procedureId, prose, plan, accepted)
			.then(() => {
				setIsSaving(false);
				onCreated(procedureId);
			})
			.catch(() => setIsSaving(false));
	};

	/**
	 * Every way out — footer, corner and mask — through one handler, because
	 * the first version guarded only the footer and the corner closed silently.
	 * `Modal.confirm` rather than `Popconfirm` for the same reason: the mask has
	 * no button to hang a popover on.
	 *
	 * Cancelling after Next deletes the pair. `DELETE /scenarios/{id}`
	 * soft-deletes the procedure with it, which is what makes an abandoned
	 * draft disposable, and the author is told so before it happens.
	 */
	const requestClose = useCallback(() => {
		/*
		  Nothing leaves mid-write: both ids are null until the chain finishes, so
		  an exit then closed while the writes carried on unwatched. Generating is
		  not blocked — it can be slow, and the service refuses to read a deleted
		  procedure, so a compile landing after the delete gets a 404.
		*/
		if (isSaving) {
			return;
		}

		// Before Next there is nothing written and nothing to delete.
		if (procedureId === null) {
			onClose(false);
			return;
		}

		/*
		  A procedure whose scenario was refused cannot be cleaned up: delete runs
		  through the scenario, and without one there is no route to it. Said
		  rather than prevented, so a failing write cannot trap the author.
		*/
		const orphaned = scenarioId === null;

		Modal.confirm({
			title: orphaned
				? 'Leave without finishing this procedure?'
				: 'Cancel and delete this procedure?',
			content: (
				<span className="bodyregular text-character-primary">
					{orphaned
						? 'It has been created but nothing routes to it yet, and it cannot be found or removed afterwards. Next will link it.'
						: 'The procedure and the scenario routing to it are removed, along with the steps Radar generated. This cannot be undone.'}
				</span>
			),
			okText: orphaned ? 'Leave' : 'Delete',
			okButtonProps: { danger: true },
			cancelText: orphaned ? 'Stay' : 'Keep editing',
			// A failed delete still closes — the thunk has said so — and the list is
			// reread, because the pair is in it.
			onOk: () =>
				orphaned
					? onClose(true)
					: removeScenario(scenarioId).then(
							() => onClose(false),
							() => onClose(true)
					  ),
		});
	}, [isSaving, onClose, procedureId, removeScenario, scenarioId]);

	const footer =
		stage === 'review' ? (
			<>
				{/* Read off the step, not re-derived from the plan. */}
				{unsupportedCount > 0 ? (
					<span className="bodyregular mr-m text-character-danger">
						{unsupportedCount === 1
							? '1 step cannot run yet'
							: `${unsupportedCount} steps cannot run yet`}
					</span>
				) : null}
				{/*
				  No Back: returning here made one Next mean "create" and the next "save
				  my edits", and recompiled over every step just removed or approved.
				  The scope can be changed afterwards on the Scenarios tab; the name
				  cannot be changed anywhere, which it could not be after leaving this
				  drawer either.
				*/}
				<Button size="large" onClick={requestClose}>
					Cancel
				</Button>
				<OWButton
					type="primary"
					size="large"
					style={{ marginLeft: 16 }}
					// No plan means the compile failed and Regenerate is the way on.
					disabled={!plan || unsupportedCount > 0}
					loading={isSaving}
					onClick={onCreateFlowchart}
				>
					Create flowchart
				</OWButton>
			</>
		) : (
			<>
				<Button size="large" onClick={requestClose} disabled={isSaving}>
					Cancel
				</Button>
				<OWButton
					type="primary"
					size="large"
					style={{ marginLeft: 16 }}
					/*
					  `isSaving` too: the check and the writes run while the stage is still
					  `form`, and OWButton only swallows a second click within one tick — so
					  a click a moment later started a second chain.
					*/
					loading={isSaving || stage === 'generating'}
					onClick={onNext}
				>
					Next
				</OWButton>
			</>
		);

	return (
		<Drawer
			visible
			placement="right"
			width="100%"
			closable={false}
			// The mask is an exit too, and it was the one that bypassed everything.
			maskClosable={!isSaving}
			bodyStyle={{ padding: 0 }}
			onClose={requestClose}
		>
			<div className="flex h-full flex-col bg-grey-1">
				<header className="flex h-12 shrink-0 items-center gap-xs border-0 border-b border-solid border-grey-4 px-m">
					<span className="bodymedium text-character-primary">
						{stage === 'review' ? 'Review the procedure' : 'Create Agent Operating Procedure'}
					</span>
					{createdName ? (
						<span className="footnoteregular text-character-secondary">{createdName}</span>
					) : null}
					<span className="flex-1" />
					{/* Gated the same way the footer's Cancel is, and confirming and
					    deleting through the same handler. */}
					<button
						type="button"
						aria-label="Close"
						disabled={isSaving}
						className="inline-flex h-7 w-7 items-center justify-center rounded-sm border border-solid border-transparent bg-transparent text-character-secondary hover:bg-grey-3 hover:text-grey-10 disabled:cursor-not-allowed disabled:text-character-disabled disabled:hover:bg-transparent"
						onClick={requestClose}
					>
						<CloseOutlined />
					</button>
				</header>

				<div className="min-h-0 flex-1 overflow-y-auto px-l py-10">
					{/* Unmounted once Next gets through — nothing comes back here. A failed
					    write leaves the stage on `form`, so a retry still has its fields. */}
					{stage === 'form' ? (
						<div className="mx-auto max-w-[680px]">
							{error ? <Alert className="mb-m" type="error" showIcon message={error} /> : null}

							<Form
								form={form}
								id={FORM_NAME}
								layout="vertical"
								requiredMark
								/*
								  The overlap was an answer about the scope as it was. Touching
								  either field makes it an answer about something else, so it
								  goes rather than sitting under a value it no longer describes.
								*/
								onValuesChange={(changed) => {
									const touched = Object.keys(changed);
									const scopeFields = [
										'problemTypeIds',
										'priorityIds',
										'allProblemTypes',
										'allPriorities',
									];

									if (touched.some((field) => scopeFields.indexOf(field) >= 0)) {
										setConflicts([]);
									}
								}}
								initialValues={{
									problemTypeIds: [],
									priorityIds: [],
									allProblemTypes: false,
									allPriorities: false,
									activeWindow: null,
								}}
							>
								<Form.Item
									name="name"
									label="Name"
									rules={[{ required: true, message: 'Please give this procedure a name.' }]}
								>
									<Input />
								</Form.Item>

								{/*
								  A Form.Item with no `name`: it draws the label and the refusal the way
								  every other field here does, while the value stays in one place.
								*/}
								<Form.Item
									label="What should Radar do? Describe it in your own words — as much detail as you want."
									required
									validateStatus={proseError ? 'error' : undefined}
									help={proseError}
								>
									<ProseColumn
										prose={prose}
										rows={8}
										onChangeProse={(next) => {
											setProse(next);
											setProseError(null);
										}}
									/>
								</Form.Item>

								<div className="mb-m border-0 border-t border-solid border-grey-4 pt-m">
									<span className="bodymedium text-character-primary">Scenarios</span>
								</div>

								<SelectWithAllToggle
									label="Problem types"
									name="problemTypeIds"
									allName="allProblemTypes"
									form={form}
									displaySelectAllWithLabel
									required
									requiredIfNotAllSelected
									selectAllPlaceholder="All problem types"
									hint={
										<ScopeOverlap
											shared={conflicts.length > 0 ? conflicts[0].shared_problem_type_ids : null}
											slice={problemTypes}
											noun="problem types"
											conflicts={conflicts}
											userType={userType}
										/>
									}
								>
									<AOPProblemTypeSelect />
								</SelectWithAllToggle>

								<SelectWithAllToggle
									label="Priorities"
									name="priorityIds"
									allName="allPriorities"
									form={form}
									displaySelectAllWithLabel
									required
									requiredIfNotAllSelected
									selectAllPlaceholder="All priorities"
									hint={
										<ScopeOverlap
											shared={conflicts.length > 0 ? conflicts[0].shared_priority_ids : null}
											slice={priorities}
											noun="priorities"
											conflicts={conflicts}
											userType={userType}
										/>
									}
								>
									<AOPPrioritySelect />
								</SelectWithAllToggle>

								<Form.Item
									name="activeWindow"
									label="Active window"
									required
									rules={[
										{
											validator: (_, value) => {
												const problem = validateActiveWindow(value);
												return problem ? Promise.reject(new Error(problem)) : Promise.resolve();
											},
										},
									]}
								>
									<AOPActiveWindowField />
								</Form.Item>
							</Form>
						</div>
					) : null}

					{stage === 'generating' ? (
						<div className="mx-auto grid max-w-[1100px] grid-cols-1 gap-12 lg:grid-cols-2">
							<ProseColumn
								prose={prose}
								label="What should Radar do?"
								rows={12}
								action={
									<Button size="small" disabled>
										Generating…
									</Button>
								}
							/>
							<div className="flex flex-col">
								<div className="mb-m flex items-center gap-s">
									<span className="footnoteMedium inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-purple-1 text-purple-6">
										AI
									</span>
									<span className="h5regular text-character-primary">
										Turning your description into a procedure
									</span>
								</div>
								{/* Their own parent: the sweep is `nth-child`, so the heading in the
								    same box put every row one position late. */}
								<div className="flex flex-col">
									{GENERATING_STAGES.map((label) => (
										<div
											key={label}
											className="aop-stage-row flex items-center gap-s border-0 border-b border-solid border-grey-3 px-2xs py-s"
										>
											{/* The same badge the AI mark wears, so the sequence reads
											    as that one thing still working. */}
											<span className="aop-stage-icons relative inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-purple-1 text-purple-6">
												<span className="aop-stage-dot" />
												<Spin className="aop-stage-spin" size="small" />
												<CheckCircleFilled className="aop-stage-tick" />
											</span>
											<span className="h5regular text-character-primary">{label}</span>
										</div>
									))}
								</div>
							</div>
						</div>
					) : null}

					{stage === 'review' ? (
						<div className="mx-auto grid max-w-[1100px] grid-cols-1 gap-12 lg:grid-cols-2">
							<div className="flex flex-col gap-m">
								{error ? <Alert type="error" showIcon message={error} /> : null}
								<ProseColumn
									prose={prose}
									label="What should Radar do?"
									rows={12}
									onChangeProse={setProse}
									action={
										<Button
											size="small"
											disabled={!procedureId}
											onClick={() => procedureId && runCompile(procedureId, prose)}
										>
											Regenerate
										</Button>
									}
								/>
							</div>

							<div className="flex flex-col gap-xs">
								{/* No plan means the compile failed; the error and Regenerate are
								    on the left, and there is nothing to list here. */}
								<span className="footnoteregular text-character-secondary">
									{!plan
										? 'No steps yet.'
										: unsupportedCount > 0
										? `${unsupportedCount} step${
												unsupportedCount === 1 ? '' : 's'
										  } cannot run. Choose an alternative or remove it before continuing.`
										: outstanding > 0
										? `${outstanding} suggestion${
												outstanding === 1 ? '' : 's'
										  } waiting. Approve them here or on the canvas.`
										: 'Every step maps to a block Radar can run.'}
								</span>

								{planViolations.map((violation, index) => (
									<AOPViolationNote key={index} violation={violation} />
								))}
								{planSuggestions.map((suggestion) => (
									<AOPSuggestionNote
										key={suggestion.id}
										suggestion={
											accepted.indexOf(suggestion.id) >= 0
												? { ...suggestion, status: 'accepted' }
												: suggestion
										}
										onAccept={() => setAccepted(accepted.concat([suggestion.id]))}
									/>
								))}

								{steps.map((step, index) => {
									const stepSuggestions = suggestionsFor(suggestions, step.name);
									const stepViolations = violationsFor(violations, step.name);
									const unsupported = step.type === 'unsupported';
									const awaiting = stepSuggestions.some(
										(suggestion) =>
											suggestion.status === 'open' && accepted.indexOf(suggestion.id) < 0
									);

									// The step carries the colour: danger if it cannot run, purple if it
									// has something to rule on, plain otherwise.
									const tint = unsupported
										? 'border-character-dangerBg bg-character-dangerBg'
										: awaiting
										? 'border-purple-2 bg-purple-1'
										: 'border-grey-4 bg-grey-1';

									return (
										<div
											key={step.name}
											className={`flex flex-col rounded border border-solid ${tint}`}
										>
											<div className="flex items-start gap-s px-2.5 py-xs">
												<span className="footnoteregular inline-flex h-5 min-w-[20px] shrink-0 items-center justify-center rounded-sm border border-solid border-grey-5 px-2xs text-grey-8">
													{stepNumberOf(index)}
												</span>
												<span className="flex min-w-0 flex-1 flex-col gap-2xs">
													<span className="bodyregular text-character-primary">
														{stepTitle(step)}
													</span>
													{unsupported ? <AOPNotExecutable /> : null}
												</span>
												{/* Refused before the click; it used to confirm and then do nothing. */}
												<Tooltip
													title={canRemoveStep ? '' : 'A procedure needs at least one step.'}
												>
													<Popconfirm
														disabled={!canRemoveStep}
														title="Remove this step? Anything pointing at it will need rewiring."
														okText="Remove"
														okButtonProps={{ danger: true }}
														cancelText="Cancel"
														onConfirm={() => removeStep(step.name)}
													>
														{/* A disabled button fires no mouse events, so the tooltip needs this. */}
														<span className="inline-flex">
															<button
																type="button"
																disabled={!canRemoveStep}
																aria-label={`Remove ${stepTitle(step)}`}
																className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-sm border border-solid border-transparent bg-transparent text-character-secondary hover:bg-grey-3 hover:text-grey-10 disabled:cursor-not-allowed disabled:text-character-disabled disabled:hover:bg-transparent disabled:hover:text-character-disabled"
															>
																<CloseOutlined />
															</button>
														</span>
													</Popconfirm>
												</Tooltip>
											</div>

											{unsupported ? (
												<div className="px-2.5 pb-xs">
													<AOPUnsupportedDetail
														step={step as AOPUnsupportedStep}
														violations={stepViolations}
														onReplace={(alternative) => replaceStep(step.name, alternative)}
														onRemove={canRemoveStep ? () => removeStep(step.name) : undefined}
													/>
												</div>
											) : null}

											{stepSuggestions.length > 0 ? (
												<div className="flex flex-col gap-xs px-2.5 pb-xs">
													{stepSuggestions.map((suggestion) => (
														<AOPSuggestionNote
															key={suggestion.id}
															compact
															suggestion={
																accepted.indexOf(suggestion.id) >= 0
																	? { ...suggestion, status: 'accepted' }
																	: suggestion
															}
															onAccept={() => setAccepted(accepted.concat([suggestion.id]))}
															// No "rejected" state exists: declining the
															// detail means dropping the step it is on.
															onDecline={canRemoveStep ? () => removeStep(step.name) : undefined}
														/>
													))}
												</div>
											) : null}
										</div>
									);
								})}
							</div>
						</div>
					) : null}
				</div>

				<footer className="flex shrink-0 items-center justify-end border-0 border-t border-solid border-grey-4 px-m py-s">
					{footer}
				</footer>
			</div>
		</Drawer>
	);
};

/**
 * What a scope collides with, under the field that collides.
 *
 * The service answers `POST /scenarios/conflicts` with the ids this scope
 * shares with each procedure already covering them, so the line can name the
 * value the author picked — "Refrigeration overlaps with …" — rather than
 * describing the collision in general and leaving them to find which of five
 * problem types caused it.
 *
 * Only the first procedure is named. An author narrows one thing at a time,
 * and a list of five is a wall rather than an instruction; the count after it
 * is there so narrowing past this one is not a surprise.
 */
const namesOf = (shared: AOPScope, slice: any, noun: string): string | null => {
	if (shared === 'all') {
		return `All ${noun}`;
	}

	if (!shared || shared.length === 0) {
		return null;
	}

	// Bracket access: the records map is keyed by id, and a `nullSafeGet` path
	// would be split on any dot inside one.
	const records = nullSafeGetOrElse('records', slice, {});
	const names = shared.map((id) => (records[id] ? records[id].name : null)).filter(Boolean);

	if (names.length === 0) {
		return null;
	}

	if (names.length === 1) {
		return names[0];
	}

	return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
};

const ScopeOverlap = ({ shared, slice, noun, conflicts, userType }: any) => {
	const subject = namesOf(shared, slice, noun);

	if (!subject || conflicts.length === 0) {
		return null;
	}

	const first = conflicts[0];
	const others = conflicts.length - 1;
	// "All problem types overlap", "Refrigeration overlaps".
	const verb = shared === 'all' || (shared.length && shared.length > 1) ? 'overlap' : 'overlaps';

	return (
		<span className="bodyregular text-character-danger">
			{subject} {verb} with{' '}
			<InlineHyperlink
				showExternalIcon
				path={`/${userType}/workflows/agent_operating_procedures/detail/${first.procedure.id}/procedure`}
				text={first.procedure.name}
			/>
			{others > 0 ? ` and ${others} other${others > 1 ? 's' : ''}` : ''}.
		</span>
	);
};

const mapDispatchToProps = (dispatch: any) => ({
	compile: (procedureId: string, prose: string) =>
		dispatch(compileProcedureDraft(procedureId, prose)),
	create: (name: string) => dispatch(createProcedure(name, null)),
	createScenario: (input: any) => dispatch(createAgentScenario(input)),
	// Cancelling after Next: deleting the scenario takes its procedure with it.
	removeScenario: (scenarioId: string) => dispatch(deleteAgentScenario(scenarioId)),
	checkConflicts: (scope: any) => dispatch(checkScenarioConflicts(scope)),
	saveDraft: (procedureId: string, prose: string, plan: AOPDraftPlan, accept: string[]) =>
		dispatch(writeWholeProcedureDraft(procedureId, prose, plan, accept)),
});

/*
  The two scope slices, to turn the ids a conflict names back into the words
  the author chose. Both are already filled by the pickers above — the
  selected values are read by id when the field mounts — so nothing is
  fetched for this.
*/
const mapStateToProps = (state: any) => ({
	problemTypes: nullSafeGetOrElse('problem_types', state, {}),
	priorities: nullSafeGetOrElse('work_order_priorities', state, {}),
});

export default withUserType(connect(mapStateToProps, mapDispatchToProps)(AOPCreateDrawer as any));
